import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Clock, Users, Calendar, AlertTriangle, Bell, Code } from 'lucide-react';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomSelect, CustomNumberInput, CustomTimeInput } from '../../shared/components/custom';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { normalizeTimeTrackingConfig, SAUDI_DEFAULT_TIMETRACKING_CONFIG } from './pointageHelpers';
import { syncTimeTrackingConfigToSettings } from './useTimeTrackingConfig';
import TimeTrackingCodesPage from './TimeTrackingCodesPage';

const DEFAULT_TIMETRACKING_CONFIG = { ...SAUDI_DEFAULT_TIMETRACKING_CONFIG };

const TimeTrackingConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.pointage.pages.timeTrackingConfigPage.${key}`, opts);
  const { patchSettings } = useSettings();
  const [timeTrackingConfig, setTimeTrackingConfig] = useState(DEFAULT_TIMETRACKING_CONFIG);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/timetracking/config');
      const raw = response?.config !== undefined ? response.config : response;
      const merged = normalizeTimeTrackingConfig(raw || {});
      setTimeTrackingConfig(merged);
      syncTimeTrackingConfigToSettings(merged, patchSettings);
    } catch (error) {
      console.error('Error loading time tracking config:', error);
      const fallback = normalizeTimeTrackingConfig({});
      setTimeTrackingConfig(fallback);
      syncTimeTrackingConfigToSettings(fallback, patchSettings);
    } finally {
      setLoading(false);
    }
  }, [patchSettings]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizeTimeTrackingConfig(timeTrackingConfig);
      const response = await localApiAdapter.put('/timetracking/config', payload);
      const savedConfig = normalizeTimeTrackingConfig(response?.config || payload);
      setTimeTrackingConfig(savedConfig);
      syncTimeTrackingConfigToSettings(savedConfig, patchSettings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving time tracking config:', error);
      alert(p('save_error') || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm(p('reset_confirm'))) {
      try {
        const response = await localApiAdapter.put('/timetracking/config/reset');
        const resetConfig = normalizeTimeTrackingConfig(response?.config || DEFAULT_TIMETRACKING_CONFIG);
        setTimeTrackingConfig(resetConfig);
        syncTimeTrackingConfigToSettings(resetConfig, patchSettings);
      } catch (error) {
        console.error('Error resetting time tracking config:', error);
        alert(p('reset_error') || t('common.error'));
      }
    }
  };

  const updateConfig = (key, value) => {
    setTimeTrackingConfig((prev) => {
      const next = normalizeTimeTrackingConfig({ ...prev, [key]: value });
      patchSettings({ timeTracking: { [key]: value } }, { persist: false });
      return next;
    });
  };

  const generalContent = (
    <>
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Clock className="h-6 w-6" />
          {p('section_general')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_work_day_start')}</label>
            <CustomTimeInput
              value={timeTrackingConfig.workDayStart}
              onChange={(v) => updateConfig('workDayStart', v)}
              placeholder="08:00"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_work_day_end')}</label>
            <CustomTimeInput
              value={timeTrackingConfig.workDayEnd}
              onChange={(v) => updateConfig('workDayEnd', v)}
              placeholder="17:00"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_lunch_break')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.lunchBreakDuration}
              onChange={(v) => updateConfig('lunchBreakDuration', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={15}
              placeholder="60"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_daily_hours')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.maxDailyHours}
              onChange={(v) => updateConfig('maxDailyHours', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="8"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_weekly_hours')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.maxWeeklyHours}
              onChange={(v) => updateConfig('maxWeeklyHours', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="48"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_ramadan_hours')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.ramadanDailyHours}
              onChange={(v) => updateConfig('ramadanDailyHours', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="6"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_prayer_break')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.prayerBreakMinutes}
              onChange={(v) => updateConfig('prayerBreakMinutes', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={5}
              placeholder="30"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_default_view')}</label>
            <CustomSelect
              value={timeTrackingConfig.defaultView}
              onChange={(v) => updateConfig('defaultView', v)}
              options={[
                { value: 'daily', label: p('view_daily') },
                { value: 'monthly', label: p('view_monthly') },
                { value: 'stats', label: p('view_stats') }
              ]}
              placeholder={p('placeholder_view')}
              searchable={true}
              creatable={false}
            />
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Clock className="h-6 w-6" />
          {p('section_overtime')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_overtime_threshold')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.overtimeThreshold}
              onChange={(v) => updateConfig('overtimeThreshold', v === '' ? 0 : Number(v))}
              step={0.5}
              min={0}
              decimals={2}
              placeholder="8"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_overtime_rate')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.overtimeRate}
              onChange={(v) => updateConfig('overtimeRate', v === '' ? 0 : Number(v))}
              step={0.1}
              min={0}
              decimals={2}
              placeholder="1.5"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_night_shift_rate')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.nightShiftRate}
              onChange={(v) => updateConfig('nightShiftRate', v === '' ? 0 : Number(v))}
              step={0.1}
              min={0}
              decimals={2}
              placeholder="2"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_friday_holiday_rate')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.fridayHolidayOvertimeRate}
              onChange={(v) => updateConfig('fridayHolidayOvertimeRate', v === '' ? 0 : Number(v))}
              step={0.1}
              min={0}
              decimals={2}
              placeholder="2"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.autoApproveOvertime}
              onChange={(e) => updateConfig('autoApproveOvertime', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_auto_approve_overtime')}</label>
          </div>
        </div>
      </div>
    </>
  );

  const absencesContent = (
    <>
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Calendar className="h-6 w-6" />
          {p('section_absences')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_annual_leave')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.maxAnnualLeave}
              onChange={(v) => updateConfig('maxAnnualLeave', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="21"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_sick_leave')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.maxSickLeave}
              onChange={(v) => updateConfig('maxSickLeave', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="120"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_maternity_leave')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.maternityLeaveWeeks}
              onChange={(v) => updateConfig('maternityLeaveWeeks', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="10"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_advance_notice')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.advanceNoticeRequired}
              onChange={(v) => updateConfig('advanceNoticeRequired', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="2"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.requireJustification}
              onChange={(e) => updateConfig('requireJustification', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_require_justification')}</label>
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6" />
          {p('section_lateness')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_lateness_threshold')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.latenessThreshold}
              onChange={(v) => updateConfig('latenessThreshold', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="15"
            />
          </div>
          <div>
            <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_max_lateness_month')}</label>
            <CustomNumberInput
              value={timeTrackingConfig.maxLatenessPerMonth}
              onChange={(v) => updateConfig('maxLatenessPerMonth', v === '' ? 0 : Math.round(Number(v)))}
              min={0}
              step={1}
              placeholder="3"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.autoGenerateWarnings}
              onChange={(e) => updateConfig('autoGenerateWarnings', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_auto_warnings')}</label>
          </div>
        </div>
      </div>
    </>
  );

  const notificationsContent = (
    <>
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Bell className="h-6 w-6" />
          {p('section_notifications')}
        </h2>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.notifyOnAbsenceRequest}
              onChange={(e) => updateConfig('notifyOnAbsenceRequest', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_notify_absence')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.notifyOnOvertimeRequest}
              onChange={(e) => updateConfig('notifyOnOvertimeRequest', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_notify_overtime')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.notifyOnLateness}
              onChange={(e) => updateConfig('notifyOnLateness', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_notify_lateness')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.emailNotifications}
              onChange={(e) => updateConfig('emailNotifications', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_email_notifications')}</label>
          </div>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Users className="h-6 w-6" />
          {p('section_display')}
        </h2>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.use24HourFormat}
              onChange={(e) => updateConfig('use24HourFormat', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_24h_format')}</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={timeTrackingConfig.showWeekNumbers}
              onChange={(e) => updateConfig('showWeekNumbers', e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label className="ml-3 text-white/80">{p('check_show_week_numbers')}</label>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabs={[
        { id: 'general', label: p('tab_general'), icon: Clock, gradient: 'from-blue-500 to-blue-600' },
        { id: 'absences', label: p('tab_absences'), icon: Calendar, gradient: 'from-teal-500 to-teal-600' },
        { id: 'codes', label: p('tab_codes'), icon: Code, gradient: 'from-indigo-500 to-indigo-600' },
        { id: 'notifications', label: p('tab_notifications'), icon: Bell, gradient: 'from-violet-500 to-violet-600' },
      ]}
      tabContent={{
        general: generalContent,
        absences: absencesContent,
        codes: <TimeTrackingCodesPage />,
        notifications: notificationsContent,
      }}
      showSaveActions={activeTab !== 'codes'}
      saved={saved}
      savedMessage={p('saved_success')}
      loading={loading}
      saving={saving}
      onSave={handleSave}
      onReset={handleReset}
    />
  );
};

export default TimeTrackingConfigPage;
